// widget-instance-actions — imperative Widget Studio lifecycle UX.
//
// CHG-2460 / ITER-905202 — Widget Studio S3 (instances CRUD).
//
// Surface:
//   - `runWidgetInstanceLifecycle(action, record)` — imperative call from the
//     generated widget-instance-detail.ts `actions` override-block onClick.
//     approve/activate/disable fire the mutation immediately; block opens a
//     confirm dialog with an optional reason (the verb is `kind: destructive,
//     requires_confirmation: true` in the entity-hint).
//   - `openWidgetInstanceEdit(record)` — opens the presentation-only edit
//     FormDialog (PATCH /admin/widgets/instances/{id}; lifecycle fields are
//     deliberately NOT editable — the BE 400s a smuggled activation_status).
//   - `<WidgetInstanceActionsHost />` — mount once at the App root. Owns the
//     react-query mutations + invalidation and renders the dialogs.
//
// Why imperative instead of hooks in the config: the generated detail
// `actions: (record) => PageHeaderAction[]` callback runs during DetailPage
// render and cannot call hooks. The single-host + module-emitter pattern (same
// as bulk-tag-dialog.tsx) keeps the mutation/invalidation in component scope
// while the hookless config only references a plain onClick. RBAC + when:
// gating stay in the config; the BE re-enforces widget-platform.* on every
// route (403 → v2-api-forbidden interceptor).

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { widgetsAdminApi } from "../api/widgets-admin";
import type { WidgetInstance } from "../api/types";
import { FormDialog, type FormFieldDef } from "../components/composition/FormDialog";

export type WidgetLifecycleAction = "approve" | "activate" | "disable" | "block";

type WidgetActionRequest =
  // approve/activate/disable — fire immediately, no dialog.
  | { kind: "immediate"; action: Exclude<WidgetLifecycleAction, "block">; record: WidgetInstance }
  // block — destructive, requires confirmation + optional reason.
  | { kind: "block"; record: WidgetInstance }
  // edit — presentation-only PATCH.
  | { kind: "edit"; record: WidgetInstance };

// ─────────────────────────────────────────────────────────────────
// Emitter: a singleton the host subscribes to. Mount exactly one host.
// ─────────────────────────────────────────────────────────────────

type Listener = (req: WidgetActionRequest) => void;
let activeListener: Listener | null = null;

export function runWidgetInstanceLifecycle(
  action: WidgetLifecycleAction,
  record: WidgetInstance,
): void {
  if (!activeListener) return; // no-op if no host mounted (dev foot-gun guard)
  if (action === "block") {
    activeListener({ kind: "block", record });
  } else {
    activeListener({ kind: "immediate", action, record });
  }
}

export function openWidgetInstanceEdit(record: WidgetInstance): void {
  if (!activeListener) return;
  activeListener({ kind: "edit", record });
}

const labelFor = (r: WidgetInstance): string => r.display_name ?? r.instance_key ?? r.id;

// ─────────────────────────────────────────────────────────────────
// Host — mount once near the App root.
// ─────────────────────────────────────────────────────────────────

export function WidgetInstanceActionsHost() {
  const [req, setReq] = useState<WidgetActionRequest | null>(null);

  useEffect(() => {
    activeListener = setReq;
    return () => {
      activeListener = null;
    };
  }, []);

  if (!req) return null;
  if (req.kind === "edit") {
    return <WidgetInstanceEditDialog record={req.record} onClose={() => setReq(null)} />;
  }
  if (req.kind === "block") {
    return <WidgetInstanceBlockDialog record={req.record} onClose={() => setReq(null)} />;
  }
  return (
    <WidgetInstanceImmediateAction
      action={req.action}
      record={req.record}
      onDone={() => setReq(null)}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared invalidation: refresh the detail record + the list.
// ─────────────────────────────────────────────────────────────────

function useInvalidateWidgetInstance() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({ queryKey: ["widget-instances", "detail", id] });
    void queryClient.invalidateQueries({ queryKey: ["widget-instances", "list"] });
  };
}

// ─────────────────────────────────────────────────────────────────
// Immediate lifecycle (approve / activate / disable).
// ─────────────────────────────────────────────────────────────────

function WidgetInstanceImmediateAction({
  action,
  record,
  onDone,
}: {
  action: Exclude<WidgetLifecycleAction, "block">;
  record: WidgetInstance;
  onDone: () => void;
}) {
  const invalidate = useInvalidateWidgetInstance();
  const mutation = useMutation({
    mutationFn: () => widgetsAdminApi[action](record.id),
    onSettled: () => {
      invalidate(record.id);
      onDone();
    },
  });

  // Fire exactly once when this action component mounts for a fresh request.
  useEffect(() => {
    mutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// ─────────────────────────────────────────────────────────────────
// Block (destructive + optional reason).
// ─────────────────────────────────────────────────────────────────

const BLOCK_FIELDS: FormFieldDef[] = [
  {
    name: "reason",
    type: "textarea",
    label: "Reason (optional)",
    props: { placeholder: "Why is this widget being blocked? (shown in the audit trail)" },
  },
];

function WidgetInstanceBlockDialog({
  record,
  onClose,
}: {
  record: WidgetInstance;
  onClose: () => void;
}) {
  const invalidate = useInvalidateWidgetInstance();
  const [values, setValues] = useState<Record<string, unknown>>({ reason: "" });
  const mutation = useMutation({
    mutationFn: (reason: string | null) => widgetsAdminApi.block(record.id, reason),
    onSuccess: () => {
      invalidate(record.id);
      onClose();
    },
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Block “${labelFor(record)}”?`}
      description="Blocking stops the widget from rendering anywhere it is embedded. You can re-activate it later."
      fields={BLOCK_FIELDS}
      values={values}
      onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
      onSubmit={async (v) => {
        const reason = String(v.reason ?? "").trim();
        await mutation.mutateAsync(reason.length ? reason : null);
      }}
      submitLabel="Block widget"
      busy={mutation.isPending}
      error={mutation.isError ? "Could not block the widget. Please try again." : undefined}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Edit (presentation fields only — PATCH /instances/{id}).
// ─────────────────────────────────────────────────────────────────

const EDIT_FIELDS: FormFieldDef[] = [
  { name: "display_name", type: "text", label: "Display name" },
  { name: "default_domain", type: "text", label: "Default domain" },
  { name: "default_url_pattern", type: "text", label: "Default URL pattern" },
  { name: "inherit_org_branding", type: "toggle", label: "Inherit org branding" },
  { name: "analytics_enabled", type: "toggle", label: "Analytics enabled" },
  { name: "ai_adaptation_enabled", type: "toggle", label: "AI adaptation enabled" },
];

function WidgetInstanceEditDialog({
  record,
  onClose,
}: {
  record: WidgetInstance;
  onClose: () => void;
}) {
  const invalidate = useInvalidateWidgetInstance();
  const [values, setValues] = useState<Record<string, unknown>>({
    display_name: record.display_name ?? "",
    default_domain: record.default_domain ?? "",
    default_url_pattern: record.default_url_pattern ?? "",
    inherit_org_branding: false,
    analytics_enabled: false,
    ai_adaptation_enabled: false,
  });
  const mutation = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      widgetsAdminApi.updateInstance(record.id, {
        display_name: (String(v.display_name ?? "").trim() || null) as string | null,
        default_domain: (String(v.default_domain ?? "").trim() || null) as string | null,
        default_url_pattern: (String(v.default_url_pattern ?? "").trim() || null) as string | null,
        inherit_org_branding: Boolean(v.inherit_org_branding),
        analytics_enabled: Boolean(v.analytics_enabled),
        ai_adaptation_enabled: Boolean(v.ai_adaptation_enabled),
      }),
    onSuccess: () => {
      invalidate(record.id);
      onClose();
    },
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Edit “${labelFor(record)}”`}
      description="Presentation settings only. Activation status changes through the lifecycle actions, not here."
      fields={EDIT_FIELDS}
      values={values}
      onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
      onSubmit={async (v) => {
        await mutation.mutateAsync(v);
      }}
      submitLabel="Save changes"
      busy={mutation.isPending}
      error={mutation.isError ? "Could not save changes. Please try again." : undefined}
    />
  );
}
